/**
 * La puerta del motor: lo que se ve cuando la cuenta ya entró pero el ordenador aún no ha abierto.
 *
 * Desde Cuentas III (11-sep-2026) casi nadie la ve: entrar con tu cuenta abre también tu motor, sin
 * contraseña. Queda para tres casos, y cada uno dice lo suyo:
 *
 *  · `migrar`   — este ordenador tenía la contraseña de antes. Se escribe UNA vez y desaparece: a
 *                 partir de ahí lo protege la cuenta, que sí se recupera.
 *  · `aviso`    — el motor dice que no: el ordenador es de otra cuenta, no hay red, la sesión caducó.
 *  · `password` — el HumanInk de este ordenador es de antes de las cuentas. Actualizarlo la quita.
 */
import { useEffect, useState } from "react";
import { entrar, ponerPassword } from "../api";

export type Paso =
  | { modo: "password"; cerrado: boolean }
  | { modo: "migrar"; mensaje: string; error?: string }
  | { modo: "aviso"; mensaje: string; codigo: string };

export default function Puerta(
  { paso, alEntrar, alMigrar, alReintentar, alSalir }: {
    paso: Paso;
    alEntrar: () => void;
    alMigrar: (password: string) => void;
    alReintentar: () => void;
    alSalir: () => void;
  },
) {
  const [password, setPassword] = useState("");
  const [repetida, setRepetida] = useState("");
  const [fallo, setFallo] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  // Cada respuesta del motor llega como un paso nuevo al MISMO componente: sin esto el botón se
  // quedaba en «Abriendo…» después de una contraseña equivocada.
  useEffect(() => { setTrabajando(false); setPassword(""); }, [paso]);

  const estrenando = paso.modo === "password" && !paso.cerrado;   // motor viejo sin contraseña puesta
  const aviso = fallo ?? (paso.modo === "migrar" ? paso.error ?? null : null);

  async function enviar() {
    if (!password.trim()) return;
    if (paso.modo === "migrar") { setTrabajando(true); alMigrar(password); return; }
    if (estrenando && password !== repetida) { setFallo("Las dos contraseñas no coinciden."); return; }
    setTrabajando(true);
    setFallo(null);
    try {
      if (estrenando) await ponerPassword(password);
      else await entrar(password);
      alEntrar();
    } catch (e) {
      setFallo(e instanceof Error ? e.message : "No se ha podido entrar.");
      setPassword("");
    } finally {
      setTrabajando(false);
    }
  }

  const marca = (
    <div className="marca">
      <img src="/humanink.png" alt="" width="26" height="26" />
      <b>HumanInk</b><span>Editor Studio</span>
    </div>
  );

  if (paso.modo === "aviso") {
    return (
      <div className="puerta">
        <section className="carta">
          {marca}
          <h2>No he podido abrir tu HumanInk<span className="sub">en este ordenador</span></h2>
          <p className="vacio">{paso.mensaje}</p>
          <div className="dir">
            <button className="btn" onClick={alReintentar}>Probar otra vez</button>
            <button className="btn fantasma" onClick={alSalir}>
              {paso.codigo === "otra_cuenta" ? "Entrar con otra cuenta" : "Cerrar sesión"}
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="puerta">
      <section className="carta">
        {marca}

        {paso.modo === "migrar" ? (
          <>
            <h2>Una última vez<span className="sub">la contraseña de este ordenador</span></h2>
            <p className="vacio">
              {paso.mensaje} Después la borro: <b>a partir de ahí tu manuscrito lo protege tu
              cuenta</b>, y si algún día pierdes el acceso, la recuperas con tu email.
            </p>
          </>
        ) : estrenando ? (
          <>
            <h2>Pon una contraseña<span className="sub">solo para este ordenador</span></h2>
            <p className="vacio">
              Tu HumanInk es de antes de las cuentas y por eso te pide una contraseña propia. Al
              actualizarlo desaparece y entras solo con tu cuenta.
            </p>
          </>
        ) : (
          <>
            <h2>Tu manuscrito está cerrado<span className="sub">entra para seguir</span></h2>
            <p className="vacio">
              La contraseña de este ordenador. Tu HumanInk es de antes de las cuentas: al
              actualizarlo, desaparece.
            </p>
          </>
        )}

        <div className="dir">
          <input
            className="escribir" type="password" autoFocus autoComplete="current-password"
            placeholder={estrenando ? "Al menos 6 caracteres" : "Tu contraseña"}
            value={password} onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !estrenando) enviar(); }}
          />
          {!estrenando && (
            <button className="btn" onClick={enviar} disabled={trabajando || !password.trim()}>
              {trabajando ? "Abriendo…" : paso.modo === "migrar" ? "Pasar a mi cuenta" : "Entrar"}
            </button>
          )}
        </div>

        {estrenando && (
          <div className="dir">
            <input
              className="escribir" type="password" autoComplete="new-password"
              placeholder="Repítela" value={repetida}
              onChange={(e) => setRepetida(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") enviar(); }}
            />
            <button className="btn" onClick={enviar} disabled={trabajando || !password.trim()}>
              {trabajando ? "Guardando…" : "Guardar y entrar"}
            </button>
          </div>
        )}

        {aviso && <p className="vacio" style={{ color: "var(--acc)" }}>{aviso}</p>}
        <div className="dir"><button className="btn fantasma" onClick={alSalir}>Cerrar sesión</button></div>
      </section>
    </div>
  );
}
